import { Add, Delete, Edit, ReceiptLong, Tune, Visibility } from "@mui/icons-material";
import {
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import FormField from "layouts/applications/wizard/components/FormField";
import ApiCalls from "services/flick_erp_api_call";

const salaryHeadTypes = [
  { label: "All", value: "all" },
  { label: "Allowance", value: "allowance" },
  { label: "Deduction", value: "deduction" },
  { label: "Employer", value: "employer" },
  { label: "Gross", value: "gross" },
  { label: "Net", value: "net" },
];

const headOptions = [
  { name: "Basic Salary", head_value: "basic", code: "BAS", head_type: "allowance" },
  { name: "House Rent Allowance", head_value: "hra", code: "HRA", head_type: "allowance" },
  { name: "Dearness Allowance", head_value: "da", code: "DA", head_type: "allowance" },
  { name: "Bonus", head_value: "bonus", code: "BON", head_type: "allowance" },
  { name: "Incentive", head_value: "incentive", code: "INC", head_type: "allowance" },
  { name: "Medical Allowance", head_value: "medical", code: "MED", head_type: "allowance" },
  { name: "Professional Tax", head_value: "ptax", code: "PTX", head_type: "deduction" },
  { name: "Tax Deducted at Source", head_value: "tds", code: "TDS", head_type: "deduction" },
  { name: "EPF Employee", head_value: "epfe", code: "EPFE", head_type: "deduction" },
  { name: "EPF Employer", head_value: "epfc", code: "EPFC", head_type: "deduction", is_employer_cost: true },
  { name: "ESIC Employee", head_value: "esice", code: "ESICE", head_type: "deduction" },
  { name: "ESIC Employer", head_value: "esicc", code: "ESIC", head_type: "deduction", is_employer_cost: true },
  { name: "Gratuity", head_value: "gratuity", code: "GRT", head_type: "deduction", is_employer_cost: true },
  { name: "Leave Deduction", head_value: "leave", code: "LOP", head_type: "deduction" },
  { name: "Other", head_value: "other", code: "OTH", head_type: "allowance" },
];

const taxCountries = [
  { label: "India", value: "india", currency: "INR" },
  { label: "Nepal", value: "nepal", currency: "NPR" },
  { label: "UAE", value: "uae", currency: "AED" },
  { label: "Europe", value: "europe", currency: "EUR" },
];

const blankSalaryHead = {
  request: "create",
  name: "",
  code: "",
  head_type: "allowance",
  head_value: "other",
  is_employer_cost: false,
};

const blankTaxSlab = {
  request: "create",
  country: "india",
  regime_name: "Default",
  currency: "INR",
  effective_from: "",
  effective_to: "",
  is_active: true,
  notes: "",
};

const blankTaxRange = {
  slab_name: "",
  min_income: "0.00",
  max_income: "",
  rate_percent: "0.000",
};

const blankSalarySlipSettings = {
  salary_slip_visibility_mode: "always_visible",
  salary_slip_visible_count: 12,
};

export function PayroleSettings({ permissions = [], modulePermissions = {} }) {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const palette = theme.palette;

  const [activeTab, setActiveTab] = useState(0);
  const [salaryTypeFilter, setSalaryTypeFilter] = useState("all");
  const [taxCountryFilter, setTaxCountryFilter] = useState("india");
  const [salaryHeads, setSalaryHeads] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [salaryHeadPayload, setSalaryHeadPayload] = useState(blankSalaryHead);
  const [taxSlabPayload, setTaxSlabPayload] = useState(blankTaxSlab);
  const [taxRangeRows, setTaxRangeRows] = useState([{ ...blankTaxRange }]);
  const [salarySlipSettings, setSalarySlipSettings] = useState(blankSalarySlipSettings);
  const [salaryHeadDialog, setSalaryHeadDialog] = useState(false);
  const [taxSlabDialog, setTaxSlabDialog] = useState(false);

  const canListSalaryHeads = permissions?.includes(modulePermissions?.salary_head_list_permission);
  const canCreateSalaryHead = permissions?.includes(modulePermissions?.salary_head_create_permission);
  const canUpdateSalaryHead = permissions?.includes(modulePermissions?.salary_head_update_permission);
  const canDeleteSalaryHead = permissions?.includes(modulePermissions?.salary_head_delete_permission);

  const canListTaxSlabs = permissions?.includes(modulePermissions?.tax_slab_list_permission) || canListSalaryHeads;
  const canCreateTaxSlab = permissions?.includes(modulePermissions?.tax_slab_create_permission) || canCreateSalaryHead;
  const canUpdateTaxSlab = permissions?.includes(modulePermissions?.tax_slab_update_permission) || canUpdateSalaryHead;
  const canDeleteTaxSlab = permissions?.includes(modulePermissions?.tax_slab_delete_permission) || canDeleteSalaryHead;

  const dialogPaperSx = {
    backgroundColor: darkMode ? palette.background.card : colors.white.main,
    boxShadow: darkMode ? "0 24px 48px rgba(2, 6, 23, 0.42)" : "none",
    width: "min(720px, calc(100vw - 32px))",
    maxWidth: "none",
    borderRadius: "16px",
    border: `1px solid ${darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.14)}`,
  };

  const filteredSalaryHeads = useMemo(() => {
    if (salaryTypeFilter === "all") return salaryHeads;
    return salaryHeads.filter((item) => item.head_type === salaryTypeFilter);
  }, [salaryHeads, salaryTypeFilter]);

  const filteredTaxSlabs = useMemo(
    () => taxSlabs.filter((item) => item.country === taxCountryFilter),
    [taxCountryFilter, taxSlabs]
  );

  const taxProfiles = useMemo(() => {
    const profileMap = new Map();
    filteredTaxSlabs.forEach((slab) => {
      const key = [
        slab.country,
        slab.regime_name,
        slab.currency,
        slab.effective_from || "",
        slab.effective_to || "",
        slab.is_active ? "active" : "inactive",
        slab.notes || "",
      ].join("|");
      if (!profileMap.has(key)) {
        profileMap.set(key, {
          id: key,
          country: slab.country,
          regime_name: slab.regime_name,
          currency: slab.currency,
          effective_from: slab.effective_from,
          effective_to: slab.effective_to,
          is_active: slab.is_active,
          notes: slab.notes,
          rows: [],
        });
      }
      profileMap.get(key).rows.push(slab);
    });
    return Array.from(profileMap.values()).map((profile) => ({
      ...profile,
      rows: profile.rows.sort((a, b) => Number(a.min_income) - Number(b.min_income)),
    }));
  }, [filteredTaxSlabs]);

  const taxProfileCountByCountry = useMemo(() => {
    const counts = {};
    taxCountries.forEach((country) => {
      const countryRows = taxSlabs.filter((item) => item.country === country.value);
      counts[country.value] = new Set(
        countryRows.map((slab) =>
          [
            slab.country,
            slab.regime_name,
            slab.currency,
            slab.effective_from || "",
            slab.effective_to || "",
            slab.is_active ? "active" : "inactive",
            slab.notes || "",
          ].join("|")
        )
      ).size;
    });
    return counts;
  }, [taxSlabs]);

  async function getSalaryHeads() {
    const res = await ApiCalls.getSalaryHead();
    if (res.success && res.code === 200) setSalaryHeads(res.data);
  }

  async function getTaxSlabs() {
    const res = await ApiCalls.getPayrollTaxSlabs();
    if (res.success && res.code === 200) setTaxSlabs(res.data);
  }

  async function getCurrencies() {
    const res = await ApiCalls.getCurrencies();
    if (res.success && res.code === 200) setCurrencies(res.data);
  }

  async function getSalarySlipSettings() {
    const res = await ApiCalls.getOrgConfigCurrent();
    if (res.success && res.code === 200) {
      setSalarySlipSettings({
        salary_slip_visibility_mode: res.data.salary_slip_visibility_mode || "always_visible",
        salary_slip_visible_count: res.data.salary_slip_visible_count || 12,
      });
    }
  }

  useEffect(() => {
    getSalaryHeads();
    getTaxSlabs();
    getCurrencies();
    getSalarySlipSettings();
  }, []);

  function openSalaryHeadDialog(payload = blankSalaryHead) {
    setSalaryHeadPayload(payload);
    setSalaryHeadDialog(true);
  }

  function openTaxSlabDialog(payload = blankTaxSlab, ranges = [{ ...blankTaxRange }]) {
    setTaxSlabPayload(payload);
    setTaxRangeRows(ranges.length ? ranges : [{ ...blankTaxRange }]);
    setTaxSlabDialog(true);
  }

  async function saveSalaryHead() {
    const payload = {
      name: salaryHeadPayload.name,
      code: salaryHeadPayload.code,
      head_type: salaryHeadPayload.head_type,
      head_value: salaryHeadPayload.head_value,
      is_employer_cost: Boolean(salaryHeadPayload.is_employer_cost),
    };
    const res =
      salaryHeadPayload.request === "create"
        ? await ApiCalls.createSalaryHead(payload, false)
        : await ApiCalls.patchSalaryHead(salaryHeadPayload.id, payload, false);
    if (res.success) {
      getSalaryHeads();
      setSalaryHeadDialog(false);
      setSalaryHeadPayload(blankSalaryHead);
    }
  }

  async function saveTaxSlab() {
    const validRows = taxRangeRows.filter((row) => row.min_income !== "" && row.rate_percent !== "");
    if (!validRows.length) return;

    const sharedPayload = {
      country: taxSlabPayload.country,
      regime_name: taxSlabPayload.regime_name || "Default",
      currency: taxSlabPayload.currency,
      effective_from: taxSlabPayload.effective_from || null,
      effective_to: taxSlabPayload.effective_to || null,
      is_active: Boolean(taxSlabPayload.is_active),
      notes: taxSlabPayload.notes || "",
      fixed_amount: "0.00",
    };

    const savedExistingIds = [];
    for (const [index, row] of validRows.entries()) {
      const rangePayload = {
        ...sharedPayload,
        slab_name: row.slab_name || `Range ${index + 1}`,
        min_income: row.min_income || "0.00",
        max_income: row.max_income || null,
        rate_percent: row.rate_percent || "0.000",
      };
      const res = row.id
        ? await ApiCalls.patchPayrollTaxSlab(row.id, rangePayload, false)
        : await ApiCalls.createPayrollTaxSlab(rangePayload, false);
      if (!res.success) return;
      if (row.id) savedExistingIds.push(row.id);
    }

    const removedIds = (taxSlabPayload.existing_row_ids || []).filter((id) => !savedExistingIds.includes(id));
    for (const id of removedIds) {
      const res = await ApiCalls.deletePayrollTaxSlab(id);
      if (!res.success) return;
    }

    if (validRows.length) {
      getTaxSlabs();
      setTaxSlabDialog(false);
      setTaxSlabPayload(blankTaxSlab);
      setTaxRangeRows([{ ...blankTaxRange }]);
    }
  }

  async function deleteSalaryHead(id) {
    const res = await ApiCalls.deleteSalaryHead(id);
    if (res.success) getSalaryHeads();
  }

  async function deleteTaxSlab(id) {
    const res = await ApiCalls.deletePayrollTaxSlab(id);
    if (res.success) getTaxSlabs();
  }

  async function deleteTaxProfile(profile) {
    for (const range of profile.rows) {
      const res = await ApiCalls.deletePayrollTaxSlab(range.id);
      if (!res.success) return;
    }
    getTaxSlabs();
  }

  async function saveSalarySlipSettings() {
    await ApiCalls.patchOrgConfigCurrent({
      salary_slip_visibility_mode: salarySlipSettings.salary_slip_visibility_mode,
      salary_slip_visible_count: Number(salarySlipSettings.salary_slip_visible_count || 12),
    });
    getSalarySlipSettings();
  }

  const salaryHeadColumns = useMemo(
    () => [
      { Header: "NAME", accessor: "name" },
      { Header: "CODE", accessor: "code" },
      {
        Header: "TYPE",
        accessor: "head_type",
        Cell: ({ row }) => <Chip size="small" label={row.original.head_type} color={row.original.head_type === "allowance" ? "success" : "warning"} />,
      },
      { Header: "VALUE", accessor: "head_value" },
      {
        Header: "EMPLOYER COST",
        accessor: "is_employer_cost",
        Cell: ({ row }) => (row.original.is_employer_cost ? "Yes" : "No"),
      },
      {
        Header: "ACTION",
        accessor: "action",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            {canUpdateSalaryHead && (
              <Tooltip title="Edit salary head">
                <IconButton
                  size="small"
                  onClick={() => openSalaryHeadDialog({ ...row.original, request: "edit" })}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDeleteSalaryHead && (
              <Tooltip title="Delete salary head">
                <IconButton size="small" color="error" onClick={() => deleteSalaryHead(row.original.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canDeleteSalaryHead, canUpdateSalaryHead]
  );

  const taxSlabColumns = useMemo(
    () => [
      {
        Header: "COUNTRY",
        accessor: "country",
        Cell: ({ row }) => taxCountries.find((item) => item.value === row.original.country)?.label || row.original.country,
      },
      { Header: "REGIME", accessor: "regime_name" },
      {
        Header: "RANGES",
        accessor: "ranges",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {row.original.rows.slice(0, 4).map((range) => (
              <Chip
                key={range.id}
                size="small"
                variant="outlined"
                label={`${range.min_income} - ${range.max_income || "No limit"} @ ${range.rate_percent}%`}
              />
            ))}
            {row.original.rows.length > 4 && <Chip size="small" label={`+${row.original.rows.length - 4} more`} />}
          </Stack>
        ),
      },
      { Header: "CURRENCY", accessor: "currency" },
      {
        Header: "ACTIVE",
        accessor: "is_active",
        Cell: ({ row }) => <Chip size="small" label={row.original.is_active ? "Active" : "Inactive"} color={row.original.is_active ? "success" : "default"} />,
      },
      {
        Header: "ACTION",
        accessor: "action",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            {canUpdateTaxSlab && (
              <Tooltip title="Edit tax slab">
                <IconButton
                  size="small"
                  onClick={() =>
                    openTaxSlabDialog(
                      {
                        country: row.original.country,
                        regime_name: row.original.regime_name,
                        currency: row.original.currency,
                        effective_from: row.original.effective_from || "",
                        effective_to: row.original.effective_to || "",
                        is_active: row.original.is_active,
                        notes: row.original.notes || "",
                        request: "edit",
                        existing_row_ids: row.original.rows.map((range) => range.id),
                      },
                      row.original.rows.map((range) => ({
                        id: range.id,
                        slab_name: range.slab_name,
                        min_income: range.min_income,
                        max_income: range.max_income || "",
                        rate_percent: range.rate_percent,
                      }))
                    )
                  }
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDeleteTaxSlab && (
              <Tooltip title="Delete tax profile">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteTaxProfile(row.original)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canDeleteTaxSlab, canUpdateTaxSlab]
  );

  const salaryHeadTable = useMemo(
    () => ({ columns: salaryHeadColumns, rows: filteredSalaryHeads }),
    [filteredSalaryHeads, salaryHeadColumns]
  );

  const taxSlabTable = useMemo(
    () => ({ columns: taxSlabColumns, rows: taxProfiles }),
    [taxProfiles, taxSlabColumns]
  );

  if (!canListSalaryHeads && !canListTaxSlabs) {
    return (
      <Card sx={{ padding: "80px" }}>
        <MDTypography align="center">Not Permitted!</MDTypography>
      </Card>
    );
  }

  return (
    <MDBox>
      <Card>
        <MDBox p={3}>
          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
            <MDBox>
              <MDTypography variant="h4">Payroll Settings</MDTypography>
              <MDTypography variant="button" color="text">
                Configure salary heads and maintain country-wise tax ranges for future payroll rules.
              </MDTypography>
            </MDBox>
            <MDBox
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(135deg, ${colors.info.main}, ${colors.primary.main})`,
                color: colors.white.main,
              }}
            >
              <Tune />
            </MDBox>
          </MDBox>

          <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)} sx={{ mt: 2 }}>
            <Tab icon={<ReceiptLong />} iconPosition="start" label="Salary Heads" />
            <Tab icon={<Tune />} iconPosition="start" label="Tax Ranges" />
            <Tab icon={<Visibility />} iconPosition="start" label="Salary Slip" />
          </Tabs>

          <Divider sx={{ my: 2 }} />

          {activeTab === 0 && canListSalaryHeads && (
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {salaryHeadTypes.map((type) => (
                    <Chip
                      key={type.value}
                      label={`${type.label} (${type.value === "all" ? salaryHeads.length : salaryHeads.filter((item) => item.head_type === type.value).length})`}
                      color={salaryTypeFilter === type.value ? "info" : "default"}
                      variant={salaryTypeFilter === type.value ? "filled" : "outlined"}
                      onClick={() => setSalaryTypeFilter(type.value)}
                    />
                  ))}
                </Stack>
                {canCreateSalaryHead && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<Add />}
                    onClick={() => openSalaryHeadDialog({ ...blankSalaryHead, head_type: salaryTypeFilter === "all" ? "allowance" : salaryTypeFilter })}
                  >
                    Add Salary Head
                  </MDButton>
                )}
              </MDBox>
              <DataTable table={salaryHeadTable} entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }} showTotalEntries isSorted={false} />
            </MDBox>
          )}

          {activeTab === 1 && canListTaxSlabs && (
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {taxCountries.map((country) => (
                    <Chip
                      key={country.value}
                      label={`${country.label} (${taxProfileCountByCountry[country.value] || 0})`}
                      color={taxCountryFilter === country.value ? "info" : "default"}
                      variant={taxCountryFilter === country.value ? "filled" : "outlined"}
                      onClick={() => setTaxCountryFilter(country.value)}
                    />
                  ))}
                </Stack>
                {canCreateTaxSlab && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<Add />}
                    onClick={() => {
                      const selectedCountry = taxCountries.find((item) => item.value === taxCountryFilter);
                      const defaultCurrency = currencies[0]?.code || selectedCountry?.currency || "INR";
                      openTaxSlabDialog(
                        { ...blankTaxSlab, country: taxCountryFilter, currency: defaultCurrency, request: "create", existing_row_ids: [] },
                        [{ ...blankTaxRange }]
                      );
                    }}
                  >
                    Add Tax Profile
                  </MDButton>
                )}
              </MDBox>
              <DataTable table={taxSlabTable} entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }} showTotalEntries isSorted={false} />
            </MDBox>
          )}

          {activeTab === 2 && (
            <MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <MDTypography variant="h6">Employee salary slip visibility</MDTypography>
                  <MDTypography variant="button" color="text">
                    Professional default is always visible, with salary amounts masked on the employee screen.
                  </MDTypography>
                  <MDBox mt={2}>
                    <FormField
                      select
                      SelectProps={{ native: true }}
                      label="Visibility Rule"
                      value={salarySlipSettings.salary_slip_visibility_mode}
                      onChange={(event) =>
                        setSalarySlipSettings((state) => ({
                          ...state,
                          salary_slip_visibility_mode: event.target.value,
                        }))
                      }
                    >
                      <option value="always_visible">Always visible</option>
                      <option value="last_x">Show last X salary slips</option>
                      <option value="hide_after_download">Hide after first download</option>
                    </FormField>
                  </MDBox>
                  {salarySlipSettings.salary_slip_visibility_mode === "last_x" && (
                    <MDBox mt={2}>
                      <FormField
                        label="Number of salary slips visible"
                        value={salarySlipSettings.salary_slip_visible_count}
                        inputProps={{ type: "number", min: 1 }}
                        onChange={(event) =>
                          setSalarySlipSettings((state) => ({
                            ...state,
                            salary_slip_visible_count: event.target.value,
                          }))
                        }
                      />
                    </MDBox>
                  )}
                  <MDBox mt={3}>
                    <MDButton variant="gradient" color="info" onClick={saveSalarySlipSettings}>
                      Save Salary Slip Settings
                    </MDButton>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={5}>
                  <MDBox
                    p={2}
                    borderRadius="lg"
                    sx={{
                      backgroundColor: darkMode ? rgba(colors.white.main, 0.04) : rgba(colors.info.main, 0.08),
                      border: `1px solid ${darkMode ? rgba(colors.white.main, 0.08) : rgba(colors.info.main, 0.18)}`,
                    }}
                  >
                    <MDTypography variant="button" fontWeight="medium">
                      Recommendation
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                      Keep slips always visible for employee records, tax filing, loans, and visa checks. The salary slip page masks amounts until the employee reveals them.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                      If you choose hide after download, records remain in backend audit but disappear from employee view after first download.
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          )}
        </MDBox>
      </Card>

      <Dialog PaperProps={{ sx: dialogPaperSx }} open={salaryHeadDialog} onClose={() => setSalaryHeadDialog(false)}>
        <DialogTitle sx={{ p: "20px" }}>
          <Typography fontWeight="600">{salaryHeadPayload.request === "create" ? "Add Salary Head" : "Edit Salary Head"}</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={6}>
              <FormField
                select
                SelectProps={{ native: true }}
                label="Preset"
                value={salaryHeadPayload.head_value || "other"}
                onChange={(event) => {
                  const selected = headOptions.find((item) => item.head_value === event.target.value);
                  setSalaryHeadPayload((state) => ({
                    ...state,
                    ...(selected || {}),
                    head_value: event.target.value,
                  }));
                }}
              >
                {headOptions.map((item) => (
                  <option key={item.head_value} value={item.head_value}>
                    {item.name}
                  </option>
                ))}
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField
                select
                SelectProps={{ native: true }}
                label="Head Type"
                value={salaryHeadPayload.head_type || "allowance"}
                onChange={(event) => setSalaryHeadPayload((state) => ({ ...state, head_type: event.target.value }))}
              >
                {salaryHeadTypes.filter((item) => item.value !== "all").map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField
                label="Name"
                value={salaryHeadPayload.name || ""}
                onChange={(event) => setSalaryHeadPayload((state) => ({ ...state, name: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField
                label="Code"
                value={salaryHeadPayload.code || ""}
                onChange={(event) => setSalaryHeadPayload((state) => ({ ...state, code: event.target.value }))}
                inputProps={{ maxLength: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" py={1}>
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium">
                    Employer cost
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    Use for employer-side PF, ESIC, gratuity, or similar salary components.
                  </MDTypography>
                </MDBox>
                <Switch
                  checked={Boolean(salaryHeadPayload.is_employer_cost)}
                  onChange={(event) => setSalaryHeadPayload((state) => ({ ...state, is_employer_cost: event.target.checked }))}
                />
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setSalaryHeadDialog(false)}>Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={saveSalaryHead}>
            {salaryHeadPayload.request === "create" ? "Create" : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog PaperProps={{ sx: dialogPaperSx }} open={taxSlabDialog} onClose={() => setTaxSlabDialog(false)}>
        <DialogTitle sx={{ p: "20px" }}>
          <Typography fontWeight="600">{taxSlabPayload.request === "create" ? "Add Tax Range" : "Edit Tax Range"}</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={6}>
              <FormField
                select
                SelectProps={{ native: true }}
                label="Country"
                value={taxSlabPayload.country || "india"}
                onChange={(event) => {
                  const country = taxCountries.find((item) => item.value === event.target.value);
                  setTaxSlabPayload((state) => ({ ...state, country: event.target.value, currency: country?.currency || state.currency }));
                }}
              >
                {taxCountries.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField label="Regime" value={taxSlabPayload.regime_name || ""} onChange={(event) => setTaxSlabPayload((state) => ({ ...state, regime_name: event.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField
                select
                SelectProps={{ native: true }}
                label="Currency"
                value={taxSlabPayload.currency || ""}
                onChange={(event) => setTaxSlabPayload((state) => ({ ...state, currency: event.target.value }))}
              >
                {(currencies.length ? currencies : [{ code: "INR", desc: "Indian Rupee" }]).map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} {currency.desc ? `- ${currency.desc}` : ""}
                  </option>
                ))}
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" py={1.5}>
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium">
                    Active
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    Inactive profiles stay saved but can be ignored later by payroll rules.
                  </MDTypography>
                </MDBox>
                <Switch checked={Boolean(taxSlabPayload.is_active)} onChange={(event) => setTaxSlabPayload((state) => ({ ...state, is_active: event.target.checked }))} />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField label="Effective From" InputLabelProps={{ shrink: true }} value={taxSlabPayload.effective_from || ""} inputProps={{ type: "date" }} onChange={(event) => setTaxSlabPayload((state) => ({ ...state, effective_from: event.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField label="Effective To" InputLabelProps={{ shrink: true }} value={taxSlabPayload.effective_to || ""} inputProps={{ type: "date" }} onChange={(event) => setTaxSlabPayload((state) => ({ ...state, effective_to: event.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <MDBox>
                  <MDTypography variant="h6">Tax Ranges</MDTypography>
                  <MDTypography variant="caption" color="text">
                    Add each income band with from amount, to amount, and tax percentage.
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setTaxRangeRows((rows) => [...rows, { ...blankTaxRange }])}
                >
                  Add Row
                </MDButton>
              </MDBox>
            </Grid>
            {taxRangeRows.map((row, index) => (
              <Grid item xs={12} key={row.id || `new-${index}`}>
                <Grid container spacing={1.5} alignItems="flex-end">
                  <Grid item xs={12} sm={3}>
                    <FormField
                      label="From Amount"
                      value={row.min_income || ""}
                      inputProps={{ type: "number" }}
                      onChange={(event) =>
                        setTaxRangeRows((rows) => rows.map((item, itemIndex) => (itemIndex === index ? { ...item, min_income: event.target.value } : item)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormField
                      label="To Amount"
                      value={row.max_income || ""}
                      inputProps={{ type: "number" }}
                      placeholder="No limit"
                      onChange={(event) =>
                        setTaxRangeRows((rows) => rows.map((item, itemIndex) => (itemIndex === index ? { ...item, max_income: event.target.value } : item)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2.5}>
                    <FormField
                      label="Tax %"
                      value={row.rate_percent || ""}
                      inputProps={{ type: "number", step: "0.001" }}
                      onChange={(event) =>
                        setTaxRangeRows((rows) => rows.map((item, itemIndex) => (itemIndex === index ? { ...item, rate_percent: event.target.value } : item)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2.5}>
                    <FormField
                      label="Label"
                      value={row.slab_name || ""}
                      placeholder={`Range ${index + 1}`}
                      onChange={(event) =>
                        setTaxRangeRows((rows) => rows.map((item, itemIndex) => (itemIndex === index ? { ...item, slab_name: event.target.value } : item)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Tooltip title="Delete range">
                      <span>
                        <IconButton
                          color="error"
                          disabled={taxRangeRows.length === 1}
                          onClick={() => setTaxRangeRows((rows) => rows.filter((item, itemIndex) => itemIndex !== index))}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <FormField label="Notes" value={taxSlabPayload.notes || ""} multiline rows={3} onChange={(event) => setTaxSlabPayload((state) => ({ ...state, notes: event.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setTaxSlabDialog(false)}>Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={saveTaxSlab}>
            {taxSlabPayload.request === "create" ? "Create" : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
